import { FC, ReactElement, JSXElementConstructor } from "react"
import { useSelector } from "react-redux"
import Lottie from "react-lottie"
import { navigateWithPrefix } from "../../utils/general_utils"
import * as loaderAnimation from "../../icons/lottie/GreenSpinner/Progress loading_circular_Green.json"
import { RootState } from "../../store/store"
import { userProfileGuardCheck } from "../../store/slices/user_profile.slice"
import { CreatorTier } from "../../utils/auth/enrich_user_data"

export const LoadingSpinner = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
    <Lottie
      options={{
        loop: true,
        autoplay: true,
        animationData: loaderAnimation,
        rendererSettings: {
          preserveAspectRatio: "xMidYMid slice"
        }
      }}
      width={48}
      height={48}
    />
  </div>
)

// Define form access configurations
export const FORM_ACCESS_CONFIG = {
  "org-email": {
    // Backward compatible: existing boolean role checks
    requiredRoles: [
      "isPaidCreator",
      "isHighValue",
      "isSalesCreator",
      "isFreeCreator",
      "isFree",
      "isOrganizer"
    ],
    // New: tier-based access (Gold, Silver, Bronze, Free who are not attendees)
    requiredTiers: [
      CreatorTier.Gold,
      CreatorTier.Silver,
      CreatorTier.Bronze,
      CreatorTier.Free
    ],
    // For org-email, Free tier users must not be attendees
    requireNonAttendeeForFreeTier: true
  },
  rc: {
    requiredRoles: ["isHighValue", "isSalesCreator"],
    requiredTiers: [CreatorTier.Gold, CreatorTier.Silver]
  }
  // Add more forms here as needed
} as const

type FormGatingProps = {
  children: ReactElement<any, string | JSXElementConstructor<any>>
  redirectUrl?: string
  formUrl?: string
}

export const FormGating: FC<FormGatingProps> = ({
  children,
  redirectUrl = "/contact-us",
  formUrl
}) => {
  const userProfile = useSelector((state: RootState) =>
    userProfileGuardCheck(state.userProfile)
  )
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )

  // Check if user profile is actually loaded (has email)
  const isUserProfileLoaded = userProfile && userProfile.email

  if (!formUrl) {
    return children
  }

  // Extract the form key from the URL (handle both exact matches and path endings)
  const getFormKey = (url: string): string | null => {
    // Check for exact matches first
    if (FORM_ACCESS_CONFIG[url as keyof typeof FORM_ACCESS_CONFIG]) {
      return url
    }

    // Check for path endings
    for (const formKey of Object.keys(FORM_ACCESS_CONFIG)) {
      if (url.endsWith(`/${formKey}`) || url.endsWith(formKey)) {
        return formKey
      }
    }

    return null
  }

  const formKey = getFormKey(formUrl)

  // If form is not configured, allow access without requiring user profile
  if (!formKey) {
    return children
  }

  const formConfig =
    FORM_ACCESS_CONFIG[formKey as keyof typeof FORM_ACCESS_CONFIG]

  // If we haven't attempted login yet, show loading
  if (!hasAttemptedLogin) {
    return <LoadingSpinner />
  }

  // If login was attempted but user is not loaded, redirect (user is not authenticated)
  if (!isUserProfileLoaded) {
    navigateWithPrefix(redirectUrl)
    return null
  }

  // Check if user has any of the required roles (backward compatible)
  const hasRequiredRole = formConfig.requiredRoles.some(
    (role) => userProfile[role as keyof typeof userProfile]
  )

  // Check if user has any of the required tiers (new tier-based access)
  const hasRequiredTier =
    formConfig.requiredTiers?.some((tier) => {
      const userTier = userProfile.creatorTier
      if (userTier !== tier) {
        return false
      }

      // For org-email, Free tier users must not be attendees
      if (
        "requireNonAttendeeForFreeTier" in formConfig &&
        formConfig.requireNonAttendeeForFreeTier === true &&
        tier === CreatorTier.Free &&
        userProfile.isAttendee
      ) {
        return false
      }

      return true
    }) ?? false

  // User must have either required role OR required tier (backward compatible)
  if (!hasRequiredRole && !hasRequiredTier) {
    navigateWithPrefix(redirectUrl)
    return null
  }

  return children
}
